import clsx from 'clsx'
import {cloneDeep, isEqual} from 'lodash'
import {FC, useEffect, useMemo, useRef, useState} from 'react'
import {KTSVG} from '../../../helpers'
import {Button} from './Button'

type Props = {
  name: string
  placeholder?: string
  className?: string
  dataOptions?: any
  valueOptions: any
  handleSetValueOptions?: any
  isSearch?: boolean
  idDelete?: boolean
  handleEventItem?: any
  hasClearValue?: boolean
}

const SelectDropdown: FC<Props> = ({
  name = '',
  placeholder = 'Search',
  className,
  dataOptions = [],
  valueOptions = '',
  handleSetValueOptions,
  isSearch = false,
  idDelete = false,
  handleEventItem,
  hasClearValue = false,
}) => {
  const [visibleMenu, setVisibleMenu] = useState(false)
  const [isClearValue, setIsClearValue] = useState(false)
  const [visibleSubMenu, setVisibleSubMenu] = useState(valueOptions)
  const filterOrderRef = useRef<any>(null)
  const [valueSearch, setValueSearch] = useState('')

  const findObjectByValue = useMemo(() => {
    return (arr, targetValue) => {
      for (const obj of arr) {
        if (isEqual(obj.value, targetValue)) {
          return obj
        }

        if (obj?.children) {
          const result = findObjectByValue(obj.children, targetValue)
          if (result.value) {
            return {parent: obj, result: result}
          }
        }
      }
      return {label: undefined, value: undefined}
    }
  }, [])

  const objectValue = useMemo(() => {
    return findObjectByValue(dataOptions, valueOptions)
  }, [dataOptions, findObjectByValue, valueOptions])

  const handleDropdownClick = () => {
    const obj = objectValue
    setVisibleSubMenu(obj?.parent?.value || obj?.value)
    setVisibleMenu(!visibleMenu)
  }

  const resetDropdown = () => {
    setVisibleMenu(false)
    setVisibleSubMenu(undefined)
  }

  const customsData = useMemo(() => {
    if (!valueSearch) return dataOptions
    return cloneDeep(dataOptions).filter(({label}) => {
      return label.toLowerCase().includes(valueSearch.toLowerCase().trim())
    })
  }, [dataOptions, valueSearch])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterOrderRef.current && !filterOrderRef.current.contains(event.target)) {
        resetDropdown()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleDeleteField = () => {
    setIsClearValue(true)
    handleSetValueOptions('')
    setTimeout(() => {
      resetDropdown()
      setIsClearValue(false)
    }, 1000)
  }

  const renderMenuItem = (data, index) => {
    const {value, label, children} = data

    if (children) {
      return (
        <div
          className={clsx('dropdown-submenu', {
            show: visibleSubMenu === value,
          })}
          key={index}
        >
          <div
            className={clsx('dropdown-submenu__item', {
              show: visibleSubMenu === value,
              'bg-light': isEqual(valueOptions, value),
            })}
            onClick={() => setVisibleSubMenu((prev) => (prev === value ? undefined : value))}
          >
            {label}
          </div>
          <div className={`dropdown-submenu__inner ${visibleSubMenu === value ? 'show' : ''}`}>
            {children.map((subitem, key) => {
              return (
                <>
                  <div
                    key={key}
                    className={clsx('dropdown-submenu__inner__item text-hover-primary', {
                      'bg-light': isEqual(valueOptions, subitem.value),
                    })}
                  >
                    <div
                      className='flex-fill text-nowrap text-truncate'
                      onClick={() => {
                        setVisibleMenu(false)
                        handleSetValueOptions(subitem.value)
                      }}
                    >
                      {subitem.label}
                    </div>
                    {idDelete && (
                      <div
                        onClick={handleEventItem}
                        className='bg-hover-danger bg-gradient rounded-circle p-2'
                      >
                        <KTSVG
                          path='/media/gori/orders/delete.svg'
                          className='opacity-50'
                          svgClassName='mh-10px'
                        />
                      </div>
                    )}
                  </div>
                </>
              )
            })}
          </div>
        </div>
      )
    }

    return (
      <div key={index} className={clsx('dropdown-submenu')}>
        <div
          className={clsx('dropdown-submenu__item', {
            'bg-light': isEqual(valueOptions, value),
          })}
        >
          <div
            className='flex-fill text-nowrap text-truncate'
            onClick={() => {
              resetDropdown()
              handleSetValueOptions(value)
            }}
          >
            {label}
          </div>
          {idDelete && (
            <div
              onClick={handleEventItem}
              className='bg-hover-danger bg-gradient rounded-circle p-2'
            >
              <KTSVG
                path='/media/gori/orders/delete.svg'
                className='opacity-50'
                svgClassName='mh-10px'
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        className={clsx('dropdown', className, {
          'border border-primary rounded-2': valueOptions,
        })}
        ref={filterOrderRef}
      >
        <div
          className={clsx('dropdown__label btn btn-sm btn-white btn-active-light-primary border')}
          onClick={handleDropdownClick}
        >
          <div className='dropdown__label__left fw-bold'>
            <span className='fw-bolder fs-5 text-gray-600'>{name}</span>
          </div>
          {valueOptions ? (
            <>
              <span className='dropdown__label__value'>
                {objectValue?.result?.label || objectValue?.label}
              </span>
              {hasClearValue && (
                <Button
                  event={handleDeleteField}
                  className='text-hover-danger border-0 ms-2 bg-transparent rounded-circle bg-hover-white py-1'
                >
                  <KTSVG
                    path='/media/gori/orders/delete.svg'
                    className='opacity-50 m-0'
                    svgClassName='mh-10px'
                  />
                </Button>
              )}
            </>
          ) : (
            <i className='bi bi-caret-down-fill ms-2'></i>
          )}
        </div>
        <div className={clsx('dropdown-menu dropdown__menu', {show: visibleMenu && !isClearValue})}>
          <div className={clsx('dropdown-submenu')}>
            {name && <div className='p-4 bg-light fw-bold fs-5'>{name}</div>}
            {isSearch && (
              <div className='mx-3 my-2'>
                <input
                  type='text'
                  autoComplete='off'
                  className='form-control'
                  placeholder={placeholder}
                  onChange={(e) => {
                    setValueSearch(e.target.value)
                  }}
                  value={valueSearch}
                  autoFocus
                />
              </div>
            )}
            {customsData.map((item, index) => renderMenuItem(item, index))}
          </div>
        </div>
      </div>
    </>
  )
}

export {SelectDropdown}
